<template>
  <VModal v-if="modelValue" v-model="modalVisible" @update:modelValue="closeModal" :click-to-close="!isLoading" :esc-to-close="!isLoading" @opened="handleModalOpened" @closed="handleModalClosed" size="xxlg" :id="'category-modal'" :z-index="9999" :hideHeader="false">

    <template #icon>
      <span class="material-symbols-outlined text-primary text-2xl">category</span>
    </template>

    <template #header-content>
      <h2 class="text-xl font-bold text-gray-800">
        {{ t(`settings.components.configuration.components.categoryModal.${currentView === 'list' ? 'list.title' : 'creator.title'}`) }} </h2>
    </template>

    <div class="h-full flex flex-col overflow-hidden relative">
      <!-- Simple Loading Overlay - Only shown on initial load -->
      <div v-if="isInitialLoading" class="absolute inset-0 bg-white/80 z-50 flex items-center justify-center">
        <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>

      <!-- Main Content -->
      <div class="flex-grow flex flex-col overflow-hidden max-h-[calc(90vh-120px)]">
        <!-- List View -->
        <CategoryList v-if="currentView === 'list'" :categories="filteredCategories" :sub-categories="subCategories" :is-loading="isLoading && !isInitialLoading" :expanded-categories="expandedCategories" :loading-sub-categories="loadingSubCategories" @toggle-category="handleToggleCategory" @category-deleted="handleCategoryDeleted" @add-new="switchView('create')" @open-prompt-manager="openPromptModal()" @open-category-prompt="openCategoryPrompt" class="overflow-auto"/>

        <!-- Create View -->
        <CategoryCreator v-else :is-submitting="isSubmitting" @save="handleSaveCategories" @cancel="switchView('list')" class="overflow-auto"/>
      </div>
    </div>
  </VModal>

  <!-- Prompt Manager Modal -->
  <PromptManagerModal v-model="showPromptModal" :selected-category="selectedPromptCategory" :all-categories="categories" @close="closePromptModal"/>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAssetStore } from '@/stores/asset'
import VModal from '@/components/modals/VModal.vue'

// Import components using the index files
import { CategoryList, CategoryCreator } from './components/category'
import { PromptManagerModal } from './components/prompt'

// Import composables
import { useCategoryManagement, usePromptManagement } from './composables'

const { t } = useI18n()
const assetStore = useAssetStore()

// Props & Emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['update:modelValue', 'update:show'])

// Local state for modal visibility and loading
const modalVisible = ref(false)
const isInitialLoading = ref(false)

// Use composables for state management
const {
  categories,
  subCategories,
  expandedCategories,
  isLoading,
  isSubmitting,
  currentView,
  loadingSubCategories,
  searchQuery,
  filteredCategories,
  loadCategories,
  clearCategories,
  toggleCategory,
  handleCategoryDeleted: deleteCategoryHandler,
  handleSaveCategories: saveCategoriesHandler,
  switchView,
} = useCategoryManagement()

const {
  showPromptModal,
  selectedCategory: selectedPromptCategory,
  openPromptModal,
  closePromptModal,
  selectCategory,
} = usePromptManagement()

// Event handlers
const handleToggleCategory = (data) => {
  toggleCategory(data)
}

const handleCategoryDeleted = async (categoryInfo) => {
  await deleteCategoryHandler(categoryInfo, assetStore.assetId, t)
}

const handleSaveCategories = async (data) => {
  const result = await saveCategoriesHandler(data, assetStore.assetId, t)

  if (result.success) {
    // Open prompt modal after creating categories
    openPromptModal()
  }
}

// Prompt management
const openCategoryPrompt = async (category) => {
  selectCategory(category)
  openPromptModal()
  await nextTick()
}

// Modal management
const closeModal = () => {
  emit('update:modelValue', false)
  emit('update:show', false)
}

// Initial data loading
const loadInitialData = async () => {
  isInitialLoading.value = true

  try {
    await loadCategories(assetStore.assetId, t)
  } finally {
    isInitialLoading.value = false
  }
}

// Lifecycle methods
const handleModalOpened = () => {
  loadInitialData()
}

const handleModalClosed = () => {
  switchView('list')
  clearCategories()
  showPromptModal.value = false
}

// Watchers
watch(() => assetStore.assetId, () => {
  if (modalVisible.value) {
    loadInitialData()
  }
})

// Sync modelValue with internal state
watch(() => props.modelValue, (newValue) => {
  modalVisible.value = newValue
}, { immediate: true })
</script>
